import React, {ReactNode} from 'react'
import {FormattedMessage} from 'react-intl'
import {Link} from 'react-router-dom'
import {defaultMediaPagesSize} from '../../config/constants'
import {intFromQuery} from '../../helpers/coreUtil'
import {useQuery, useQueryObj} from '../hocs/hooks'
import styles from './TextPager.scss'

const defaultPageKey = 'page'
const pagePlaceHolder = '...'

const genArray = (currentPage: number, sizeMax: number, nPages: number) => {
  const lower = Math.max(0, currentPage - sizeMax)
  const upper = Math.max(Math.min(nPages - 1, currentPage + sizeMax), currentPage)
  return [
    ...lower > 0
      ? lower === 1 ? [0] : [0, pagePlaceHolder]
      : [],
    ...[...Array(upper - lower + 1).keys()].map(x => x + lower),
    ...upper < nPages - 1
      ? upper === nPages - 2 ? [nPages - 1] : [pagePlaceHolder, nPages - 1]
      : [],
  ]
}

//0-based
const getPageFromProps = ({pageKey = defaultPageKey, onChange, page}: {
  pageKey?: string
  page?: number
  onChange?: (page: number) => any
}, query: ReturnType<typeof useQuery>) => onChange
  ? page!
  : Math.max(intFromQuery(query[pageKey], 1) - 1, 0)

const AdaptiveLink = ({page, queryObj, onChange, pageKey, children, noScroll, ...props}: {
  page: number
  queryObj: ReturnType<typeof useQueryObj>
  onChange?: (page: number) => any
  pageKey: string
  children: ReactNode
  className?: string
  noScroll?: boolean
}) => onChange
  ? <button {...props} onClick={() => onChange(page)}>
    {children}
  </button>
  : <Link to={queryObj({[pageKey]: page === 0 ? undefined : page + 1}, noScroll ? undefined : {keepScrollPos: false})} {...props}>
    {children}
  </Link>
AdaptiveLink.displayName = 'AdaptiveLink'

// There are controlled and uncontrolled modes, specified by onChange.
// onChange: undefined -> uncontrolled mode
// otherwise -> controlled mode

// in controlled mode, page must be defined, and when page changes (is clicked), pager will call onChange and will not change URL.
const TextPager = (props: {
  total: number
  pageSize?: number
  pageKey?: string
  page?: number // required if notQuery is on. 0-based index
  onChange?: (page: number) => any
  sideMax?: number
  noScroll?: boolean
}) => {
  const {onChange, pageSize = defaultMediaPagesSize, total, pageKey = defaultPageKey, sideMax = 2} = props
  const query = useQuery()
  const currentPage = getPageFromProps(props, query)
  const queryObj = useQueryObj()
  const nPages = Math.trunc((total + pageSize - 1) / pageSize)
  const disableLeft = currentPage === 0
  const disableRight = currentPage >= nPages - 1
  return total > pageSize || !!currentPage
    ? <div className="d-flex flex-column">
      {/* SP 前次ボタン */}
      <div className="d-flex d-md-none justify-content-center align-items-center">
        <ul className={['no-show-when-enter d-flex pl-0',
          styles.list].join(' ')}>
          <li className="mr-2">
            <AdaptiveLink onChange={onChange}
                          noScroll={props.noScroll}
                          pageKey={pageKey}
                          queryObj={queryObj}
                          className={disableLeft ? styles.disable : ''}
                          page={currentPage - 1}
                          aria-label="Previous">
              <button aria-hidden="true"
                      className={['btn btn-outline-media', styles.button].join(' ')}>
                <FormattedMessage id="pager.previous" defaultMessage="Previous page"/>
              </button>
              <span className="sr-only">Previous</span>
            </AdaptiveLink>
          </li>
          <li className="ml-2">
            <AdaptiveLink onChange={onChange}
                          noScroll={props.noScroll}
                          pageKey={pageKey}
                          queryObj={queryObj}
                          className={disableRight ? styles.disable : ''}
                          page={currentPage + 1}
                          aria-label="Next">
              <span aria-hidden="true"
                    className={['btn btn-outline-media', styles.button].join(' ')}>
                <FormattedMessage id="pager.next" defaultMessage="Next page"/>
              </span>
              <span className="sr-only">Next</span>
            </AdaptiveLink>
          </li>
        </ul>
      </div>
      <div>
        <ul className={['justify-content-center align-items-center no-show-when-enter d-flex mb-0 pl-0',
          styles.list].join(' ')}>
          {/* PC 前ボタン */}
          <li className="pr-2 mr-3 d-none d-md-flex">
            <AdaptiveLink onChange={onChange}
                          noScroll={props.noScroll}
                          pageKey={pageKey}
                          queryObj={queryObj}
                          className={disableLeft ? styles.disable : ''}
                          page={currentPage - 1}
                          aria-label="Previous">
              <button aria-hidden="true"
                      className={['btn btn-outline-media', styles.button].filter(Boolean).join(' ')}>
                <FormattedMessage id="pager.previous"/>
              </button>
              <span className="sr-only">Previous</span>
            </AdaptiveLink>
          </li>
          {
            genArray(currentPage, sideMax, nPages)
              .map((page, index) => page === pagePlaceHolder
                ? <li key={`disabled-${index}`} className="page-item ml-1">
                  <button
                    className={[styles.middleCircle, 'text-media rounded-circle bg-white'].join(' ')}
                  >
                    {page}
                  </button>
                </li>
                : <li key={page} className={[`page-item rounded-circle d-flex justify-content-center align-items-center
                ${page === currentPage && ['active bg-media'].join(' ')}`, styles.circle].filter(Boolean).join(' ')}>
                  <AdaptiveLink onChange={onChange}
                                  noScroll={props.noScroll}
                                  pageKey={pageKey}
                                  queryObj={queryObj}
                                  className={[`${page === currentPage ? 'text-white text-decoration-none' : 'text-media'}`,
                                    styles.ls].filter(Boolean).join(' ')}
                                  page={page}
                  >
                    {page + 1}
                  </AdaptiveLink>
              </li>
              )
          }
          {/* PC 次ボタン */}
          <li className="pl-3 ml-3 d-none d-md-flex">
            <AdaptiveLink onChange={onChange}
                          noScroll={props.noScroll}
                          pageKey={pageKey}
                          queryObj={queryObj}
                          className={disableRight ? styles.disable : ''}
                          page={currentPage + 1}
                          aria-label="Next">
              <span aria-hidden="true"
                    className={['btn btn-outline-media', styles.button].join(' ')}>
                <FormattedMessage id="pager.next"/>
              </span>
              <span className="sr-only">Next</span>
            </AdaptiveLink>
          </li>
        </ul>
      </div>
  </div>
    : null
}
TextPager.displayName = 'TextPager'
export default TextPager
